// Privacy Operations (platform) — the DSAR request queue + default data-retention
// policies. Real data from compliance-service.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Card, Table, Tag, Tabs, Typography, Space, Button, Select, Modal, Form, InputNumber,
  Input, Switch, message, Tooltip,
} from 'antd';
import { SafetyCertificateOutlined, ReloadOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import {
  getPrivacyRequests, setRequestStatus, getRetentionPolicies, upsertRetentionPolicy,
  fulfilExport, downloadExport,
  type PrivacyRequest, type RetentionPolicy,
} from '../../services/privacyOpsService';

const EXPORTABLE = new Set(['access', 'export', 'portability']);

const { Title, Text } = Typography;

const STATUS = ['pending', 'in_review', 'approved', 'completed',
  'completed_with_retentions', 'partially_fulfilled', 'blocked_by_containment',
  'rejected'];
const statusColor: Record<string, string> = {
  pending: 'gold', in_review: 'blue', approved: 'cyan', completed: 'green', rejected: 'red',
  // Deliberately NOT green. The request was closed with participants that never
  // confirmed erasure, so it must not read as success at a glance; details.erasure
  // names which ones are outstanding.
  completed_with_retentions: 'orange',
  // NOT a closed state. Destructive document fan-out was contained, so no
  // participant was contacted and nothing was erased or retained — the request
  // is still open and must be re-run once containment is lifted. Red would read
  // as rejected and orange as a partial result; this is neither.
  blocked_by_containment: 'volcano',
  // ⚠️ NOT a closed state, and deliberately not the same colour as
  // completed_with_retentions. At least one participant's outcome is UNKNOWN —
  // unreachable, unconfigured, failed or unverifiable — so this is outstanding
  // work, not a lawful retention decision. These used to be recorded as
  // completed_with_retentions, which claimed a decision nobody made about
  // services that were never contacted.
  partially_fulfilled: 'magenta',
};
const typeColor: Record<string, string> = {
  access: 'blue', export: 'geekblue', correct: 'purple',
  delete: 'red', restrict: 'orange', portability: 'cyan',
};
const fmt = (s: string) => (s ? fmtDateTime(s) : '—');
const short = (id: string) => (id ? id.slice(0, 8) : '—');

const DSARQueue: React.FC = () => {
  const [rows, setRows] = useState<PrivacyRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string | undefined>(undefined);

  const load = (status = filter) => {
    setLoading(true);
    getPrivacyRequests(status)
      .then(setRows)
      .catch((e) => message.error(e?.response?.data?.message || 'Could not load requests.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [busy, setBusy] = useState<string | null>(null);

  const changeStatus = async (id: string, status: string) => {
    try {
      await setRequestStatus(id, status);
      message.success(`Marked ${status.replace(/_/g, ' ')}`);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not update.');
    }
  };

  const runFulfil = async (id: string) => {
    setBusy(id);
    try {
      await fulfilExport(id);
      message.success('Export built — the request is now completed.');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not build export.');
    } finally {
      setBusy(null);
    }
  };

  const runDownload = async (id: string) => {
    try {
      await downloadExport(id);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not download.');
    }
  };

  return (
    <Card
      title={`Data-subject requests (${rows.length})`}
      extra={
        <Space>
          <Select
            allowClear
            placeholder="All statuses"
            style={{ width: 150 }}
            value={filter}
            onChange={(v) => { setFilter(v); load(v); }}
            options={STATUS.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
          />
          <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>Refresh</Button>
        </Space>
      }
    >
      <Table<PrivacyRequest>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={{ pageSize: 12 }}
        columns={[
          { title: 'Raised', dataIndex: 'created_at', render: fmt, width: 170 },
          {
            title: 'Type', dataIndex: 'request_type',
            render: (t: string) => <Tag color={typeColor[t] || 'default'}>{t}</Tag>,
          },
          { title: 'Tenant', dataIndex: 'tenant_id', render: short },
          { title: 'User', dataIndex: 'user_id', render: short },
          {
            title: 'Status', dataIndex: 'status',
            render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s.replace(/_/g, ' ')}</Tag>,
          },
          {
            title: 'Update', key: 'action',
            render: (_: unknown, r: PrivacyRequest) => (
              <Select
                size="small"
                style={{ width: 130 }}
                value={r.status}
                onChange={(v) => changeStatus(r.id, v)}
                options={STATUS.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
              />
            ),
          },
          {
            title: 'Export', key: 'export',
            render: (_: unknown, r: PrivacyRequest) =>
              EXPORTABLE.has(r.request_type) ? (
                r.status === 'completed' ? (
                  <Button size="small" icon={<DownloadOutlined />} onClick={() => runDownload(r.id)}>
                    Download
                  </Button>
                ) : (
                  <Button
                    size="small"
                    type="primary"
                    ghost
                    loading={busy === r.id}
                    onClick={() => runFulfil(r.id)}
                  >
                    Build export
                  </Button>
                )
              ) : (
                <Tooltip title="Auto-export applies to access/export/portability requests.">
                  <span className="text-muted-foreground">—</span>
                </Tooltip>
              ),
          },
        ]}
      />
    </Card>
  );
};

const COMMON_CATEGORIES = [
  'applicants', 'audit_logs', 'payroll', 'employee_records', 'leave_records',
  'documents', 'email', 'notifications',
];

const RetentionPanel: React.FC = () => {
  const [rows, setRows] = useState<RetentionPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    getRetentionPolicies()
      .then(setRows)
      .catch((e) => message.error(e?.response?.data?.message || 'Could not load policies.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const v = await form.validateFields();
    try {
      await upsertRetentionPolicy({
        data_category: v.data_category.trim(),
        retention_days: v.keep_forever ? null : (v.retention_days ?? null),
        legal_hold: !!v.legal_hold,
      });
      message.success('Retention policy saved');
      setOpen(false);
      form.resetFields();
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not save policy.');
    }
  };

  return (
    <Card
      title="Default retention policies"
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setOpen(true); }}>Set policy</Button>}
    >
      <Text type="secondary">
        Platform-wide defaults for how long each data category is kept. (Enforcement is
        a later phase — these define the policy.)
      </Text>
      <Table<RetentionPolicy>
        className="mt-3"
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: 'Data category', dataIndex: 'data_category' },
          {
            title: 'Retention', dataIndex: 'retention_days',
            render: (d: number | null) => (d == null ? <Tag>Keep indefinitely</Tag> : `${d} days`),
          },
          {
            title: 'Legal hold', dataIndex: 'legal_hold',
            render: (h: boolean) => (h ? <Tag color="red">On hold</Tag> : '—'),
          },
          { title: 'Updated', dataIndex: 'updated_at', render: fmt },
          {
            title: '', key: 'edit',
            render: (_: unknown, r: RetentionPolicy) => (
              <Button
                size="small"
                onClick={() => {
                  form.setFieldsValue({
                    data_category: r.data_category,
                    retention_days: r.retention_days ?? undefined,
                    keep_forever: r.retention_days == null,
                    legal_hold: r.legal_hold,
                  });
                  setOpen(true);
                }}
              >
                Edit
              </Button>
            ),
          },
        ]}
      />

      <Modal title="Retention policy" open={open} onOk={submit} onCancel={() => setOpen(false)} okText="Save" destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="data_category" label="Data category" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="employee_records"
              options={COMMON_CATEGORIES.map((c) => ({ value: c, label: c }))}
            />
          </Form.Item>
          <Form.Item name="keep_forever" label="Keep indefinitely" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(p, c) => p.keep_forever !== c.keep_forever}
          >
            {({ getFieldValue }) =>
              getFieldValue('keep_forever') ? null : (
                <Form.Item name="retention_days" label="Retain for (days)" rules={[{ required: true }]}>
                  <InputNumber min={1} style={{ width: '100%' }} placeholder="e.g. 2555 (7 years)" />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item
            name="legal_hold"
            label={<Tooltip title="Suspends deletion regardless of the retention period.">Legal hold</Tooltip>}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export const PrivacyOpsPage: React.FC = () => (
  <div className="p-1">
    <Space align="center" className="mb-4">
      <SafetyCertificateOutlined style={{ fontSize: 22 }} />
      <Title level={3} style={{ margin: 0 }}>Privacy Operations</Title>
    </Space>
    <Tabs
      defaultActiveKey="dsar"
      items={[
        { key: 'dsar', label: 'DSAR queue', children: <DSARQueue /> },
        { key: 'retention', label: 'Retention', children: <RetentionPanel /> },
      ]}
    />
  </div>
);

export default PrivacyOpsPage;
